import React, { useState } from 'react';
import styled from 'styled-components';
import { ArrowUpRight, Github, Linkedin, Mail, Sparkles } from 'lucide-react';
import PublicContactSection from '../shared/public-contact-section';
import { SkillProjectsPopup, useSkillProjects } from '../../components/skill-projects-popup';

const Page = styled.div`
  --bg: #0b0d11;
  --surface: rgba(255, 255, 255, 0.04);
  --surface-strong: rgba(255, 255, 255, 0.08);
  --text: #f7f3eb;
  --muted: rgba(247, 243, 235, 0.68);
  --line: rgba(247, 243, 235, 0.12);
  --accent: #d0a76f;
  min-height: 100vh;
  color: var(--text);
  background:
    radial-gradient(circle at top right, rgba(208, 167, 111, 0.16), transparent 28%),
    linear-gradient(180deg, #11141b 0%, var(--bg) 42%, #08090c 100%);

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const Shell = styled.div`
  width: min(100% - 3rem, 1320px);
  margin: 0 auto;
  padding: 4rem 0 6rem;

  @media (max-width: 640px) {
    width: min(100% - 2rem, 1320px);
    padding: 2rem 0 4rem;
  }
`;

const Hero = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(280px, 0.55fr);
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const HeroPanel = styled.div`
  padding: clamp(2rem, 4vw, 4rem);
  border-radius: 32px;
  border: 1px solid var(--line);
  background:
    linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02)),
    rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(12px);
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.28);
  min-width: 0;
`;

const Label = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.76rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 1rem;
`;

const Name = styled.h1`
  margin: 0;
  max-width: 14ch;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(3rem, 6vw, 5.8rem);
  line-height: 1;
  letter-spacing: -0.04em;
`;

const Summary = styled.p`
  margin: 1.25rem 0 0;
  max-width: 44rem;
  color: var(--muted);
  font-size: 1.05rem;
  line-height: 1.8;
  white-space: pre-line;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MetaCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.5rem;
  border-radius: 20px;
  background: var(--surface);
  border: 1px solid var(--line);
  min-width: 0;
  transition: border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(208, 167, 111, 0.38);
    background: var(--surface-strong);
  }
`;

const MetaLabel = styled.div`
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--muted);
`;

const MetaValue = styled.div`
  margin-top: 0.35rem;
  font-size: 1rem;
  overflow-wrap: anywhere;
`;

const ProfileCard = styled.div`
  padding: 1.5rem;
  border-radius: 32px;
  border: 1px solid var(--line);
  background: rgba(255,255,255,0.03);
  display: flex;
  align-items: flex-end;
  min-width: 0;

  @media (max-width: 860px) {
    align-items: stretch;
  }
`;

const LinkStack = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 1.2rem;
  border-radius: 18px;
  text-decoration: none;
  color: var(--text);
  background: var(--surface);
  border: 1px solid var(--line);
  width: 100%;
  min-width: 0;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(208, 167, 111, 0.55);
    background: var(--surface-strong);
  }

  &:focus-visible {
    outline: 3px solid rgba(208, 167, 111, 0.4);
    outline-offset: 3px;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  gap: 2rem;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.section`
  padding: clamp(1.5rem, 3vw, 2.5rem);
  border-radius: 28px;
  border: 1px solid var(--line);
  background: rgba(255,255,255,0.03);
  min-width: 0;
`;

const SectionTitle = styled.h2`
  margin: ${({ $withTopMargin }) => ($withTopMargin ? '2rem 0 1.25rem' : '0 0 1.25rem')};
  font-size: 1rem;
  line-height: 1.2;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent);
  display: flex;
  align-items: center;
  gap: 0.85rem;

  &::after {
    content: '';
    width: 2.5rem;
    height: 1px;
    background: currentColor;
    opacity: 0.55;
  }
`;

const Timeline = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const TimelineItem = styled.article`
  padding: 1.75rem;
  border-radius: 20px;
  background: var(--surface);
  border: 1px solid var(--line);
  min-width: 0;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ItemTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.3;
`;

const ItemMeta = styled.div`
  color: var(--muted);
  font-size: 0.92rem;
  margin-top: 0.3rem;
`;

const ItemCopy = styled.p`
  margin: 0.75rem 0 0;
  color: var(--muted);
  line-height: 1.7;
`;

const SkillCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Skill = styled.button`
  padding: 0.75rem 1rem;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface-strong);
  color: var(--text);
  font: inherit;
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(208, 167, 111, 0.65);
    background: rgba(208, 167, 111, 0.16);
  }

  &:focus-visible {
    outline: 3px solid rgba(208, 167, 111, 0.4);
    outline-offset: 3px;
  }
`;

const Projects = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const Project = styled.article`
  overflow: hidden;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03));
  border: 1px solid var(--line);
`;

const ProjectCover = styled.div`
  aspect-ratio: 16 / 7;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(208, 167, 111, 0.2), rgba(255, 255, 255, 0.04));
  border-bottom: 1px solid var(--line);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProjectBody = styled.div`
  padding: 1.75rem;
`;

const Avatar = styled.img`
  width: 84px;
  height: 84px;
  margin-bottom: 1.5rem;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(208, 167, 111, 0.7);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.22);
`;

const ProjectLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 1.25rem;
`;

const ProjectLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  text-decoration: none;
  color: var(--text);
  border-bottom: 1px solid rgba(208, 167, 111, 0.45);
  padding-bottom: 0.15rem;

  &:hover {
    color: var(--accent);
  }
`;

const FullWidth = styled.div`
  margin-top: 2rem;
`;

function titleFromLinks(links = {}) {
  const rawEntries = Object.entries(links || {}).filter(([, value]) => Boolean(value));
  return rawEntries.map(([key, value]) => ({
    key,
    label: key.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
    value,
  }));
}

function projectLinks(project) {
  const links = Array.isArray(project?.links) ? project.links.filter((link) => link?.url) : [];
  if (project?.github_url) links.push({ url: project.github_url, title: 'GitHub' });
  if (project?.live_url) links.push({ url: project.live_url, title: 'Live project' });
  return links;
}

function formatDate(dateValue) {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function DateRange({ start, end }) {
  const startLabel = formatDate(start);
  const endLabel = formatDate(end);
  if (!startLabel && !endLabel) return null;
  return <div>{startLabel || '—'} — {endLabel || (end ? '—' : 'Present')}</div>;
}

function ProfileAvatar({ profile }) {
  const picture = typeof profile?.picture === 'string' ? profile.picture.trim() : '';
  const [isVisible, setIsVisible] = useState(Boolean(picture));

  if (!isVisible) return null;
  return (
    <Avatar
      src={picture}
      alt={profile?.profile_name || profile?.username || 'Profile'}
      onError={() => setIsVisible(false)}
    />
  );
}

function ProjectCoverImage({ project }) {
  const [isVisible, setIsVisible] = useState(Boolean(project?.cover_image_url));

  return (
    <ProjectCover>
      {isVisible ? (
        <img
          src={project.cover_image_url}
          alt={project.title || 'Project cover'}
          onError={() => setIsVisible(false)}
        />
      ) : (
        <span>{project?.title || 'Project'}</span>
      )}
    </ProjectCover>
  );
}

export default function OpusTemplate({ data }) {
  const { selectedSkill, isPopupVisible, handleSkillClick, handleClosePopup } = useSkillProjects();

  if (!data) return null;

  const profile = data.profile || {};
  const experiences = Array.isArray(data.experiences) ? data.experiences.filter(Boolean) : [];
  const educations = Array.isArray(data.educations) ? data.educations.filter(Boolean) : [];
  const skills = Array.isArray(data.skills) ? data.skills.filter((skill) => skill?.name) : [];
  const projects = Array.isArray(data.projects) ? data.projects.filter(Boolean) : [];
  const certifications = Array.isArray(data.certifications) ? data.certifications.filter(Boolean) : [];
  const organisations = Array.isArray(data.organisations) ? data.organisations.filter(Boolean) : [];
  const socialLinks = titleFromLinks(profile?.links);
  const getKey = (item, index) => item?.id || `${item?.name || item?.title || 'entry'}-${index}`;

  return (
    <Page>
      <Shell>
        <Hero>
          <HeroPanel>
            <ProfileAvatar profile={profile} />
            <Label><Sparkles size={14} /> Portfolio</Label>
            {(profile.profile_name || profile.username) && <Name>{profile.profile_name || profile.username}</Name>}
            {profile.bio && <Summary>{profile.bio}</Summary>}
            <MetaGrid>
              {profile.email && <MetaCard>
                <Mail size={18} color="var(--accent)" aria-hidden="true" />
                <div>
                <MetaLabel>Email</MetaLabel>
                <MetaValue>{profile.email}</MetaValue>
                </div>
              </MetaCard>}
            </MetaGrid>
          </HeroPanel>

          <ProfileCard>
            <LinkStack>
              {profile?.email && (
                <SocialLink href={`mailto:${profile.email}`}>
                  <span><Mail size={16} /> Email</span>
                  <ArrowUpRight size={16} />
                </SocialLink>
              )}
              {profile?.linkedin && (
                <SocialLink href={profile.linkedin} target="_blank" rel="noreferrer">
                  <span><Linkedin size={16} /> LinkedIn</span>
                  <ArrowUpRight size={16} />
                </SocialLink>
              )}
              {profile?.github && (
                <SocialLink href={profile.github} target="_blank" rel="noreferrer">
                  <span><Github size={16} /> GitHub</span>
                  <ArrowUpRight size={16} />
                </SocialLink>
              )}
              {socialLinks
                .filter((item) => !['linkedin', 'github', 'email'].includes(item.key.toLowerCase()))
                .slice(0, 2)
                .map((item) => (
                  <SocialLink key={item.key} href={item.value} target="_blank" rel="noreferrer">
                    <span>{item.label}</span>
                    <ArrowUpRight size={16} />
                  </SocialLink>
                ))}
            </LinkStack>
          </ProfileCard>
        </Hero>

        <SectionGrid>
          {experiences.length > 0 && (
            <Section>
              <SectionTitle>Selected Experience</SectionTitle>
              <Timeline>
                {experiences.slice(0, 4).map((experience, index) => (
                  <TimelineItem key={getKey(experience, index)}>
                    <ItemHeader>
                      <ItemTitle>{experience.role || experience.name}</ItemTitle>
                      <DateRange start={experience.start_date} end={experience.end_date} />
                    </ItemHeader>
                    <ItemMeta>{[experience.company, experience.location].filter(Boolean).join(' • ')}</ItemMeta>
                    {experience.description && <ItemCopy>{experience.description}</ItemCopy>}
                  </TimelineItem>
                ))}
              </Timeline>
            </Section>
          )}

          {(skills.length > 0 || educations.length > 0) && (
            <Section>
              {skills.length > 0 && (
                <>
                  <SectionTitle>Capabilities</SectionTitle>
                  <SkillCloud>
                    {skills.map((skill, index) => (
                      <Skill key={getKey(skill, index)} type="button" onClick={() => handleSkillClick(skill)}>
                        {skill.name}
                      </Skill>
                    ))}
                  </SkillCloud>
                </>
              )}

              {educations.length > 0 && (
                <>
                  <SectionTitle $withTopMargin={skills.length > 0}>Education</SectionTitle>
                  <Timeline>
                    {educations.slice(0, 3).map((education, index) => (
                      <TimelineItem key={getKey(education, index)}>
                        <ItemTitle>{education.degree || education.name}</ItemTitle>
                        <ItemMeta>{[education.field_of_study, education.university].filter(Boolean).join(' • ')}</ItemMeta>
                      </TimelineItem>
                    ))}
                  </Timeline>
                </>
              )}
            </Section>
          )}
        </SectionGrid>

        {projects.length > 0 && (
          <FullWidth>
            <Section>
              <SectionTitle>Projects</SectionTitle>
              <Projects>
                {projects.map((project, index) => (
                  <Project key={getKey(project, index)}>
                    <ProjectCoverImage project={project} />
                    <ProjectBody>
                    <ItemHeader>
                      <ItemTitle>{project.title || 'Untitled project'}</ItemTitle>
                      {Array.isArray(project.skills) && project.skills.length > 0 && <div>{project.skills.length} skills</div>}
                    </ItemHeader>
                    {project.description && <ItemCopy>{project.description}</ItemCopy>}
                    {projectLinks(project).length > 0 && <ProjectLinks>
                      {projectLinks(project).map((link, linkIndex) => (
                        <ProjectLink key={`${getKey(project, index)}-link-${linkIndex}`} href={link.url} target="_blank" rel="noopener noreferrer">
                          {link.title || link.label || 'Open'} <ArrowUpRight size={14} />
                        </ProjectLink>
                      ))}
                    </ProjectLinks>}
                    </ProjectBody>
                  </Project>
                ))}
              </Projects>
            </Section>
          </FullWidth>
        )}

        {certifications.length > 0 && (
          <FullWidth>
            <Section>
              <SectionTitle>Certifications</SectionTitle>
              <Timeline>
                {certifications.map((certification, index) => (
                  <TimelineItem key={getKey(certification, index)}>
                    <ItemTitle>{certification.title || certification.name}</ItemTitle>
                    {certification.issuer && <ItemMeta>{certification.issuer}</ItemMeta>}
                    {certification.credential_url && (
                      <ProjectLink href={certification.credential_url} target="_blank" rel="noopener noreferrer">
                        View credential <ArrowUpRight size={14} />
                      </ProjectLink>
                    )}
                  </TimelineItem>
                ))}
              </Timeline>
            </Section>
          </FullWidth>
        )}

        {organisations.length > 0 && (
          <FullWidth>
            <Section>
              <SectionTitle>Organisations</SectionTitle>
              <Timeline>
                {organisations.map((organisation, index) => (
                  <TimelineItem key={getKey(organisation, index)}>
                    <ItemTitle>{organisation.name || organisation.title}</ItemTitle>
                    {organisation.role && <ItemMeta>{organisation.role}</ItemMeta>}
                    {organisation.description && <ItemCopy>{organisation.description}</ItemCopy>}
                    <DateRange start={organisation.start_date} end={organisation.end_date} />
                  </TimelineItem>
                ))}
              </Timeline>
            </Section>
          </FullWidth>
        )}

        <FullWidth>
          <PublicContactSection
            profile={profile}
            eyebrow="Commission"
            title="Discuss a role, project, or collaboration"
            subtitle="This section sends a message directly into the portfolio owner's inbox view inside Portify."
            palette={{
              surface: 'rgba(255,255,255,0.03)',
              card: 'rgba(255,255,255,0.04)',
              input: 'rgba(255,255,255,0.03)',
              border: 'rgba(247, 243, 235, 0.12)',
              text: '#f7f3eb',
              muted: 'rgba(247, 243, 235, 0.7)',
              iconBg: 'rgba(208, 167, 111, 0.18)',
              iconFg: '#f5d5a7',
              buttonBg: '#d0a76f',
              buttonText: '#1a1410',
              shadow: '0 30px 80px rgba(0, 0, 0, 0.24)',
            }}
          />
        </FullWidth>
      </Shell>
      <SkillProjectsPopup
        visible={isPopupVisible}
        onHide={handleClosePopup}
        skill={selectedSkill}
      />
    </Page>
  );
}
